/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars */
import React, { useMemo, useEffect, useState } from "react";
import { Button } from "@shared/ui/button";
import {
  ChevronLeft,
  Send,
  AlertTriangle,
  Info,
  User,
  FileText,
  Lock,
  CheckCircle2,
  Download,
  AlertCircle,
  ArrowLeft,
  Users,
  Check,
  XCircle,
  Paperclip,
  Eye,
} from "lucide-react";
import { StepHelpers } from "@shared/components/Stepper";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { Consumer } from "./sharedAccountSelection";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useUserNames,
  useChannelChoices,
  useNoticeTemplates,
  useSaveBulkCommunicationStep3,
  useBulkCommunicationResult,
  useBulkCommunicationStep1,
  useDownloadBulkCommunicationReport,
  useDownloadBulkCommunicationErrors,
} from "@features/cx/outstanding/hooks";
import { formatFileSize } from "@features/cx/shared/utils/file";

import { useNavigate } from "react-router-dom";
interface SharedReviewSendProps {
  type: "notice" | "reminder";
  data?: {
    approvedBy?: string;
    approvalNotes?: string;
  };
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
}

const getInitials = (name?: string) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

export const SharedReviewSend: React.FC<SharedReviewSendProps> = ({
  type,
  data,
  onDataChange,
  onNext,
  onPrevious,
  stepHelpers,
  currentStepIndex = 2,
}) => {
  const navigate = useNavigate();
  const [isDispatched, setIsDispatched] = useState(false);
  const [dispatchResults, setDispatchResults] = useState<any>(null);
  const [batchId, setBatchId] = useState<number | string | null>(null);

  const remoteUtilityId = useRemoteUtilityId();

  // Retrieve step data
  const step1Data = useMemo(() => {
    if (stepHelpers) {
      return stepHelpers.getStepData<{ selectedConsumers?: Consumer[]; isSelectAll?: boolean }>(0);
    }
    return null;
  }, [stepHelpers]);

  const step2Data = useMemo(() => {
    if (stepHelpers) {
      return stepHelpers.getStepData<{
        communicationChannel?: string;
        noticeTemplate?: string | number;
        reminderTemplate?: string | number;
        issueTemplate?: string | number;
        templateName?: string;
        attachments?: Array<{ name: string; size: number; type: string }>;
      }>(1);
    }
    return null;
  }, [stepHelpers]);

  const { mutateAsync: saveStep3, isPending: isSaving } =
    useSaveBulkCommunicationStep3();
  const { data: usersData, isLoading: loadingUsers } = useUserNames({
    remoteUtilityId,
  });

  const [resultPage, setResultPage] = useState(1);

  const { mutate: downloadReport, isPending: isDownloadingReport } = useDownloadBulkCommunicationReport();
  const { mutate: downloadErrors, isPending: isDownloadingErrors } = useDownloadBulkCommunicationErrors();

  const { data: apiResultData, isLoading: isLoadingResult } =
    useBulkCommunicationResult({
      id: batchId || "",
      remoteUtilityId: remoteUtilityId || "",
      page: resultPage,
      limit: PAGE_SIZE,
    });

  const normalizedResults = useMemo(() => {
    if (!apiResultData && !dispatchResults) return null;

    if (apiResultData) {
      return apiResultData.results ?? apiResultData;
    }

    return dispatchResults;
  }, [apiResultData, dispatchResults]);
console.log(normalizedResults, "noramlized")
  const supervisorOptions = useMemo(
    () =>
      usersData?.result?.map((data: any) => ({
        value: data.name,
        label: data.name,
      })) || [],
    [usersData],
  );

  // Load persistence
  const savedData = stepHelpers?.getStepData<{
    approvedBy?: string;
    approvalNotes?: string;
  }>(currentStepIndex);

  const defaultValues = {
    approvedBy: savedData?.approvedBy || data?.approvedBy || "",
    approvalNotes: savedData?.approvalNotes || data?.approvalNotes || "",
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "approvedBy",
        label: "Approved By",
        type: "select",
        required: true,
        placeholder: loadingUsers
          ? "Loading supervisors..."
          : "— Select supervisor —",
        options: supervisorOptions,
        className: "col-span-12 md:col-span-6",
      },
      {
        name: "approvalNotes",
        label: "Approval Notes",
        type: "text",
        placeholder: "Optional remarks...",
        className: "col-span-12 md:col-span-6",
      },
    ],
    [supervisorOptions, loadingUsers],
  );

  const approvalForm = FormService.createForm<any>({
    fields: formFields,
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    approvalForm.reset(defaultValues);
  }, []);



  const attachments = useMemo(() => {
    const stepFiles = step2Data?.attachments || [];
    const windowFiles = (window as any).bulkNoticeFileData?.fileObjects || [];
    const mappedWindowFiles = windowFiles.map((f: File) => ({
      name: f.name,
      size: f.size,
      type: f.type,
    }));
    
    // Combine and deduplicate
    const combined = [...stepFiles];
    mappedWindowFiles.forEach((wf: any) => {
      if (!combined.some((cf) => cf.name === wf.name && cf.size === wf.size)) {
        combined.push(wf);
      }
    });
    return combined;
  }, [step2Data]);

  const handleViewFile = (file: { name: string; size: number }) => {
    const fileObjects = (window as any).bulkNoticeFileData?.fileObjects || [];
    const fileObject = fileObjects.find(
      (f: File) => f.name === file.name && f.size === file.size,
    );
    if (fileObject) {
      const url = URL.createObjectURL(fileObject);
      const win = window.open(url, "_blank");
      if (win) {
        win.focus();
      }
    } else {
      alert("Attachment source file not available for preview.");
    }
  };

  const selectedConsumers = step1Data?.selectedConsumers || [];

  const channelVal = step2Data?.communicationChannel || "";

  const selectedChannelType = useMemo(() => {
    if (!channelVal) return "";
    const str = channelVal.toString().toUpperCase();
    if (str === "0" || str === "EMAIL") return "EMAIL";
    if (
      str === "1" ||
      str === "SMS" ||
      str === "TEXT MESSAGE" ||
      str === "TEXT_MESSAGE"
    )
      return "SMS";
    return "";
  }, [channelVal]);

  const { data: channelList } = useChannelChoices("quick_messaging");
  const { data: templateList } = useNoticeTemplates(selectedChannelType);

  const channelDisplayName = useMemo(() => {
    if (!channelVal) return "N/A";
    const found = channelList?.find(
      (c: any) => String(c.key) === String(channelVal),
    );
    return found ? found.value : channelVal;
  }, [channelVal, channelList]);

  const templateVal =
    step2Data?.noticeTemplate ||
    step2Data?.reminderTemplate ||
    step2Data?.issueTemplate ||
    "";

  console.log("step2 data", step2Data);

  // Prefer stored templateName (always present after fix), fallback to lookup
  const templateDisplayName = useMemo(() => {
    if (!templateVal) return "N/A";
    if (step2Data?.templateName) return step2Data.templateName;
    const found = templateList?.find(
      (t: any) => String(t.id) === String(templateVal),
    );
    return found ? found.templateName : String(templateVal);
  }, [templateVal, templateList, step2Data?.templateName]);

  const channel = channelDisplayName;
  const template = templateDisplayName;

  const totalOutstanding = useMemo(() => {
    return selectedConsumers.reduce((sum, c) => sum + c.totalOutstanding, 0);
  }, [selectedConsumers]);

  // Auto-calculated fields
  const refNo = useMemo(() => {
    const prefix = type === "notice" ? "NTC" : "RMD";
    return `${prefix}-${Math.floor(1000000 + Math.random() * 9000000)}`;
  }, [type]);

  const dueDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14); // 14 days payment window
    return d.toISOString().split("T")[0]; // Format like 2026-06-10
  }, []);

  // Sync state
  const handleFieldChange = (name: string, value: any) => {
    if (value === undefined || value === null) return;
    const currentValues = approvalForm.getValues();
    const updated = { ...currentValues, [name]: value };
    onDataChange?.(updated);
    if (stepHelpers) {
      stepHelpers.setStepData(currentStepIndex, {
        ...updated,
        refNo,
        paymentDueBy: dueDateStr,
        totalOutstanding,
      });
    }
  };

  const currentApprovedBy = approvalForm.watch("approvedBy");

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 5;

  const consumerIdsArray = useMemo(() => {
    return selectedConsumers.map(c => c.consumerId);
  }, [selectedConsumers]);

  const { data: step1ApiResponse, isLoading: isLoadingConsumers } = useBulkCommunicationStep1({
    remoteUtilityId: remoteUtilityId || "",
    page: currentPage,
    limit: pageSize,
    searchData: searchTerm,
    consumerIds: step1Data?.isSelectAll ? undefined : consumerIdsArray
  });

  const paginatedConsumers = step1ApiResponse?.results || [];
  const totalItems = step1ApiResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const currency=useCurrency();
  const recipientColumns: TableColumn<Consumer>[] = useMemo(
    () => [
      {
        header: "Consumer Details",
        key: "fullName",
        render: (value: any, row: Consumer) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold shrink-0">
              {getInitials(row.fullName)}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-800">
                {row.fullName}
              </span>
              <span className="text-xs text-gray-400">{row.consumerNo}</span>
            </div>
          </div>
        ),
      },
      {
        header: "Outstanding",
        key: "totalOutstanding",
        render: (value: any) => (
          <div className="text-sm font-bold text-blue-600">
            {currency} {Number(value).toFixed(2)}
          </div>
        ),
      },
    ],
    [],
  );

  const handleComplete = async () => {
    const approvalNotesVal = approvalForm.getValues("approvalNotes") || "";
    const selectedSupervisorOpt = supervisorOptions.find(
      (opt) => String(opt.value) === String(currentApprovedBy),
    );
    const approvedByName = selectedSupervisorOpt
      ? selectedSupervisorOpt.label
      : currentApprovedBy ||
        (supervisorOptions && supervisorOptions[0]?.label) ||
        "";

    // Build FormData payload matching step-3 endpoint requirements
    const formData = new FormData();
    formData.append(
      "consumer_ids",
      JSON.stringify(selectedConsumers.map((c) => c.consumerId)),
    );
    formData.append("batch_type", type === "notice" ? "1" : "2");
    formData.append("channel_type", String(channelVal));
    formData.append("template_id", String(Number(templateVal)));
    formData.append("template_name", template);
    formData.append("total_outstanding", totalOutstanding.toFixed(2));
    formData.append("approval_notes", approvedByName);
    formData.append("remarks", approvalNotesVal);
    formData.append("remote_utility_id", String(remoteUtilityId));

    // Append attachments if any from window object store
    const fileObjects = (window as any).bulkNoticeFileData?.fileObjects || [];
    fileObjects.forEach((file: File) => {
      formData.append("attachments", file);
    });
    try {
      const response = await saveStep3(formData);

      const newBatchId =
        response?.batchId ??
        response?.result?.batchId ??
        response?.id ??
        response?.result?.id ??
        response?.batch_id;
      if (newBatchId) {
        setBatchId(newBatchId);
      }

      const totalCount = selectedConsumers.length;
      const failedCount = totalCount > 0 ? 1 : 0;
      const dispatchedCount = Math.max(0, totalCount - failedCount);

      const failedItems: Array<{ consumer: Consumer; error: string }> = [];
      if (totalCount > 0) {
        failedItems.push({
          consumer: selectedConsumers[0],
          error:
            channel === "Email"
              ? "Invalid Email Address Format"
              : channel === "SMS"
                ? "Missing Mobile Contact Details"
                : "Delivery Address Incomplete",
        });
      }

      setDispatchResults({
        recipients: selectedConsumers,
        template,
        channel,
        approvedBy: approvedByName,
        refNo,
        totalCount,
        dispatchedCount,
        failedCount,
        failedItems,
      });

      setIsDispatched(true);
    } catch (error) {
      console.error("Failed to submit step 3 bulk communication:", error);
    }
  };

  const handleDone = () => {
    const storageKey =
      type === "notice"
        ? `bulk-notice-wizard-progress-${remoteUtilityId}`
        : `bulk-reminders-wizard-progress-${remoteUtilityId}`;
    localStorage.removeItem(storageKey);
    navigate("/cx/outstanding");
  };

  const onDownloadReport = () => {
    if (!batchId || !remoteUtilityId) return;
    downloadReport(
      { batchId, remoteUtilityId },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Notice_Batch_Full_Report_${normalizedResults?.refNo || batchId}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }
    );
  };

  const onDownloadErrorReport = () => {
    if (!batchId || !remoteUtilityId) return;
    downloadErrors(
      { batchId, remoteUtilityId },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Notice_Batch_Error_Report_${normalizedResults?.refNo || batchId}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }
    );
  };


  const columns: TableColumn<Consumer>[] = useMemo(
    () => [
      {
        header: "Consumer Name",
        key: "name",
        render: (value: any) => (
          <span className="font-semibold text-neutral-800">{value}</span>
        ),
      },
      {
        header: "Account No.",
        key: "consumerNo",
        render: (value: any) => (
          <span className="text-neutral-400">{value}</span>
        ),
      },
      {
        header: "Outstanding",
        key: "outstandingAmount",
        render: (value: any) => (
          <span className="font-bold text-neutral-700">
            {currency} {Number(value).toFixed(2)}
          </span>
        ),
      },
      {
        header: "Delivery Status",
        key: "deliveryStatus",
        render: (value: any, row: any) => {
          if (value === "Failed" || value === "Error") {
            return (
              <div className="flex items-center gap-1.5 text-red-600 font-semibold">
                <XCircle size={14} className="text-red-500" />
                <span>Failed: {row.errorMessage || "Delivery error"}</span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
              <Check size={14} className="text-emerald-500" />
              <span>Dispatched via {row.deliveryChannel || normalizedResults?.channel}</span>
            </div>
          );
        },
      },
    ],
    [normalizedResults],
  );

  if (isDispatched && batchId && isLoadingResult) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white border border-gray-200 rounded-xl shadow-sm min-h-[300px] my-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-sm text-gray-500 mt-4 font-semibold">
          Fetching dispatch delivery report logs...
        </p>
      </div>
    );
  }

  if (isDispatched && normalizedResults) {
    return (
      <div className="space-y-6 animate-in fade-in zoom-in-95 duration-500">
        {/* Results Page Header */}
        <div className="flex justify-between items-center bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              <h1 className="text-xl font-bold text-neutral-800 leading-none">
                Bulk {type === "notice" ? "Notice" : "Reminder"} Dispatch
                Completed
              </h1>
            </div>
            <p className="text-sm text-neutral-400 mt-1.5 pl-8">
              {type === "notice" ? "Notice" : "Reminder"} logs successfully
              committed. View batch statistics and delivery report status logs
              below.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onDownloadReport}
              disabled={isDownloadingReport}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 text-sm font-semibold rounded-lg h-9"
            >
              <Download size={14} /> {isDownloadingReport ? "Downloading..." : "Download Report"}
            </Button>
            {apiResultData?.summary?.failed > 0 && (
              <Button
                onClick={onDownloadErrorReport}
                disabled={isDownloadingErrors}
                className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 flex items-center gap-1.5 text-sm font-semibold rounded-lg h-9"
              >
                <AlertCircle size={14} /> {isDownloadingErrors ? "Downloading..." : "Download Error Report"}
              </Button>
            )}
          </div>
        </div>

        {/* Stats tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <QuickStatsCard
            title="Total Recipients"
            count={apiResultData?.summary?.total}
            trend={"neutral" as any}
            icon={<Users className="h-8 w-8 text-blue-500" />}
          />
          <QuickStatsCard
            title="Dispatched"
            count={apiResultData?.summary?.delivered}
            trend={"up" as any}
            icon={<CheckCircle2 className="h-8 w-8 text-emerald-500" />}
          />
          <QuickStatsCard
            title="Failed"
            count={apiResultData?.summary?.failed}
            trend={"down" as any}
            icon={<XCircle className="h-8 w-8 text-rose-500" />}
          />
        </div>

        {/* Metadata summary */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h4 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-4">
            Batch Metadata Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Batch Reference No.
              </span>
              <span className="font-semibold text-neutral-800">
                {apiResultData?.metadata?.refNo ?? 'NA'}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Delivery Channel
              </span>
              <span className="font-semibold text-neutral-800">
                {apiResultData?.metadata?.channel ?? "NA"}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Approved By
              </span>
              <span className="font-semibold text-neutral-800">
                {apiResultData?.metadata?.approvedBy ?? 'NA'}
              </span>
            </div>
            <div className="space-y-0.5">
              <span className="text-gray-400 block font-medium">
                Template Applied
              </span>
              <span className="font-semibold text-neutral-800">
                {apiResultData?.metadata?.templateName ?? 'NA'}
              </span>
            </div>
          </div>
        </div>

        {/* Recipients results logs table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden p-5 space-y-4">
          <h4 className="text-sm font-black text-gray-400 uppercase tracking-widest">
            {type === "notice" ? "Notice" : "Reminder"} Delivery logs
          </h4>
          <div className="border border-gray-150 rounded-lg overflow-hidden">
            <GenericTable<Consumer>
              data={normalizedResults || []}
              columns={columns}
              rowKey="consumerId"
              selectable={false}
              pagination={
                apiResultData
                  ? {
                      currentPage: resultPage,
                      totalPages: Math.ceil(
                        (apiResultData.count || normalizedResults.totalCount || normalizedResults.total_count || 0) / PAGE_SIZE,
                      ),
                      totalItems: apiResultData.count || normalizedResults.totalCount || normalizedResults.total_count || 0,
                      itemsPerPage: PAGE_SIZE,
                      startItem: (resultPage - 1) * PAGE_SIZE + 1,
                      endItem: Math.min(
                        resultPage * PAGE_SIZE,
                        apiResultData.count || normalizedResults.totalCount || normalizedResults.total_count || 0,
                      ),
                    }
                  : undefined
              }
              onPageChange={setResultPage}
            />
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-gray-100">
          <Button onClick={handleDone}>
            <span>Done</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Banner */}
      <div className="flex items-center gap-2 bg-blue-50/50 border border-blue-100 text-blue-800 text-sm p-2.5 rounded-md">
        <Info size={14} className="text-blue-500 flex-shrink-0" />
        <span>
          Review all details carefully. Once you click{" "}
          <strong>
            {type === "notice" ? "Send Notices" : "Send Reminders"}
          </strong>
          , a formal {type === "notice" ? "notice" : "reminder"} record is
          created for each recipient. This action cannot be undone.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Panel: Recipients */}
        <div className="lg:col-span-7 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-auto min-h-[280px]">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-1.5 text-sm font-bold text-gray-800">
            <User size={14} className="text-gray-500" />
            Recipients{" "}
            <span className="text-gray-400 font-normal ml-1">
              ({selectedConsumers.length})
            </span>
          </div>

          <div className="flex-1 p-2 overflow-x-auto">
            <GenericTable
              data={paginatedConsumers}
              columns={recipientColumns}
              rowKey="consumerId"
              onSearch={(val) => {
                setSearchTerm(val);
                setCurrentPage(1);
              }}
              searchPlaceholder="Search recipients..."
              pagination={{
                currentPage,
                itemsPerPage: pageSize,
                totalItems: totalItems,
                totalPages: totalPages,
                startItem: totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1,
                endItem: Math.min(currentPage * pageSize, totalItems),
              }}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          {/* Details Summary */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-1.5 text-sm font-bold text-gray-800">
              <FileText size={14} className="text-gray-500" />
            {type === "notice" ? "Notice Details" : "Reminder Details"}
          </div>
          <div className="p-4 space-y-4 text-sm flex-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Template</span>
              <span className="font-semibold text-gray-800">
                {step2Data?.templateName}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Communication Channel</span>
              <span className="font-semibold text-gray-800">{channel}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Payment Due By</span>
              <span className="font-semibold text-gray-800">{dueDateStr}</span>
            </div>
            {type === "notice" && attachments.length > 0 && (
              <div className="border-t border-gray-100 my-2 pt-3 space-y-2">
                <span className="text-gray-500 block font-medium">
                  Attachments ({attachments.length})
                </span>
                <div className="space-y-1.5 max-h-[120px] overflow-y-auto pr-1">
                  {attachments.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-gray-50 p-1.5 pl-2 rounded border border-gray-100 text-sm"
                    >
                      <div className="flex items-center gap-1.5 truncate mr-2">
                        <Paperclip
                          size={12}
                          className="text-gray-400 shrink-0"
                        />
                        <span
                          className="font-medium text-gray-700 truncate"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm text-gray-400">
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewFile(file);
                          }}
                          className="p-1 hover:bg-gray-200 text-gray-400 hover:text-blue-600 rounded transition-colors"
                          title="View attachment"
                        >
                          <Eye size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t border-gray-100 my-2 pt-4 flex justify-between items-center">
              <span className="text-gray-500">Total Outstanding</span>
              <span className="font-bold text-gray-800">
                {currency} {totalOutstanding.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Supervisor Approval Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-auto">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-1.5 text-sm font-bold text-gray-800">
              <Lock size={14} className="text-gray-500" />
              Supervisor Approval
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2 bg-amber-50/50 border border-amber-200 text-amber-800 text-sm p-2.5 rounded-md">
                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                <span>
                  Formal {type === "notice" ? "notices" : "reminders"} are legal
                  documents. A supervisor must approve before dispatch. The
                  approver's name will be logged on every{" "}
                  {type === "notice" ? "notice" : "reminder"} record.
                </span>
              </div>

              <div className="pt-2">
                <DynamicForm
                  form={approvalForm}
                  fields={formFields}
                  gridColumns={12}
                  config={{
                    classes: FIELD_CLASSES,
                    showSubmitButton: false,
                  }}
                  onFieldChange={handleFieldChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-100">
        <Button
          onClick={onPrevious}
          className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 font-medium flex items-center gap-1.5 rounded-lg px-4 py-2"
        >
          <ChevronLeft size={16} />
          <span>
            Previous:{" "}
            {type === "notice" ? "Notice Details" : "Channel & Template"}
          </span>
        </Button>

        <Button
          onClick={handleComplete} // Trigger complete flow in stepper
          disabled={!currentApprovedBy || isSaving}
          variant="default"
        >
          <Send size={16} />
          <span>
            {isSaving
              ? "Sending..."
              : type === "notice"
                ? "Send Notices"
                : "Send Reminders"}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default SharedReviewSend;
